import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, Checkbox, Chip, IconButton, InputAdornment, List, ListItemButton, ListItemIcon, ListItemText,
    MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import GridViewIcon from '@mui/icons-material/GridView';
import TranslateIcon from '@mui/icons-material/Translate';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { ConfigCard } from '../style/cards';

// Translation rows that ship with the platform — admins can override the
// per-language values but cannot delete them. Custom rows live alongside.
const REQUIRED_TRANSLATIONS = [
    { key: 'FLD_GIFT_VOUCHER',            field: 'Gift voucher',             nl: 'Cadeaubon',              en: 'Gift voucher',              fr: 'Bon cadeau' },
    { key: 'FLD_VALUE',                   field: 'Value',                    nl: 'Waarde',                 en: 'Value',                     fr: 'Valeur' },
    { key: 'FLD_VALID_TO_INFO',           field: 'Valid until',              nl: 'Geldig tot en met',      en: 'Valid until',               fr: 'Valable jusqu\'au' },
    { key: 'FLD_CHEQUE_NUMBER',           field: 'Cheque number',            nl: 'Bonnummer',              en: 'Cheque number',             fr: 'Numéro de chèque' },
    { key: 'FLD_CHEQUE_CHECKSUM',         field: 'Cheque checksum',          nl: 'Controle cijfer',        en: 'Cheque checksum',           fr: 'Somme de contrôle' },
    { key: 'FLD_TERM',                    field: 'Terms & conditions',       nl: '',                       en: '',                          fr: '' },
    { key: 'FLD_COMMERCIAL_DISCOUNT',     field: 'Commercial discount',      nl: 'Commerciële korting',    en: 'Commercial discount',       fr: 'Remise commerciale' },
    { key: 'FLD_CUSTOMER_DISCOUNT',       field: 'Customer discount',        nl: 'Klantenkorting',         en: 'Customer discount',         fr: 'Remise client' },
    { key: 'FLD_CUSTOMER_DISCOUNT_COUPON',field: 'Customer discount coupon', nl: 'Kortingsbon voor klanten', en: 'Customer discount coupon', fr: 'Bon de réduction client' },
];

const CadeaubonTab = ({ theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    // 0 = Settings, 1 = Voucher layouts, 2 = Translations
    const [voucherTab, setVoucherTab] = useState(0);

    const [config, setConfig] = useState({
        // SETTINGS
        availability: {
            showInNavigation: true,
            allowPhysical: true,
            allowDigital: true,
        },
        amounts: {
            presets: [50, 100, 150],
            allowCustom: true,
            customMin: 10,
            customMax: 250,
        },
        discounts: {
            countTowardTotals: false,
            applyToVoucherOnly: false,
            applyToMixedOrders: false,
        },
        shipping: {
            cost: 7,
            freeFrom: 100,
        },
        personalization: {
            allowPersonalMessage: 'digital-only', // 'never' | 'digital-only' | 'all'
            sendDigitalDirectly: true,
        },
        // VOUCHER LAYOUTS
        physicalLayouts: [
            { id: 'p1', name: 'Cheque layout',  preview: '#000' },
        ],
        digitalLayouts: [
            { id: 'd1', name: '[Default] Customer voucher',  preview: '#000' },
            { id: 'd2', name: 'Cheque layout verjaardag',    preview: '#e8d4b3' },
            { id: 'd3', name: 'Cheque layout verjaarda…',    preview: '#e8d4b3' },
            { id: 'd4', name: 'Binome_test',                  preview: '#fff' },
            { id: 'd5', name: 'Binome_test_2',                preview: '#000' },
            { id: 'd6', name: 'Cheque layout gratis',         preview: '#e8b787' },
        ],
        systemDefaults: [
            { id: 's1', name: '[Default] Gift voucher' },
            { id: 's2', name: '[Default] Deposit' },
            { id: 's3', name: '[Default] Voucher' },
            { id: 's4', name: '[Default] Customer credit' },
            { id: 's5', name: '[Default] Commercial' },
            { id: 's6', name: '[Default] Discount Ticket' },
            { id: 's7', name: '[Default] Customer cou…' },
        ],
        // TRANSLATIONS — required entries from REQUIRED_TRANSLATIONS get
        // their per-language overrides here. Custom rows live as full objects.
        translationOverrides: {},  // { FLD_KEY: { nl, en, fr } }
        customTranslations: [
            { key: 'FLD_PROMO_FOOTER', field: 'Promo footer', nl: 'Geniet van je voordeel!', en: 'Enjoy your perk!', fr: '' },
        ],
        translationsSearch: '',
    });

    const setSection = (sec) => (patch) => setConfig(prev => ({ ...prev, [sec]: { ...prev[sec], ...patch } }));

    // ────── SETTINGS handlers ──────
    const setAvailability    = setSection('availability');
    const setAmounts         = setSection('amounts');
    const setDiscounts       = setSection('discounts');
    const setShipping        = setSection('shipping');
    const setPersonalization = setSection('personalization');

    const addPreset = (val) => {
        const n = parseInt(val);
        if (!Number.isFinite(n) || n <= 0) return;
        if (config.amounts.presets.includes(n)) return;
        setAmounts({ presets: [...config.amounts.presets, n].sort((a, b) => a - b) });
    };
    const removePreset = (val) => {
        setAmounts({ presets: config.amounts.presets.filter(p => p !== val) });
    };

    // ────── VOUCHER LAYOUTS handlers ──────
    const addPhysicalLayout = () => setConfig(prev => ({
        ...prev,
        physicalLayouts: [...prev.physicalLayouts, { id: `p_${Date.now()}`, name: 'New layout', preview: '#000' }],
    }));
    const addDigitalLayout = () => setConfig(prev => ({
        ...prev,
        digitalLayouts:  [...prev.digitalLayouts,  { id: `d_${Date.now()}`, name: 'New layout', preview: '#000' }],
    }));
    const renameLayout = (kind, id, name) => setConfig(prev => ({
        ...prev,
        [kind]: prev[kind].map(l => l.id === id ? { ...l, name } : l),
    }));
    const removeLayout = (kind, id) => setConfig(prev => ({
        ...prev,
        [kind]: prev[kind].filter(l => l.id !== id),
    }));
    const duplicateSystemDefault = (defaultId) => {
        const src = config.systemDefaults.find(s => s.id === defaultId);
        if (!src) return;
        setConfig(prev => ({
            ...prev,
            digitalLayouts: [...prev.digitalLayouts, { id: `d_${Date.now()}`, name: `${src.name.replace('[Default]', '')} (copy)`.trim(), preview: '#000' }],
        }));
    };

    // ────── TRANSLATIONS handlers ──────
    const setOverride = (key, lang, val) => setConfig(prev => ({
        ...prev,
        translationOverrides: {
            ...prev.translationOverrides,
            [key]: { ...(prev.translationOverrides[key] || {}), [lang]: val },
        },
    }));
    const setCustomField = (idx, lang, val) => setConfig(prev => ({
        ...prev,
        customTranslations: prev.customTranslations.map((c, i) => i === idx ? { ...c, [lang]: val } : c),
    }));
    const setCustomFieldName = (idx, val) => setConfig(prev => ({
        ...prev,
        customTranslations: prev.customTranslations.map((c, i) => i === idx ? { ...c, field: val } : c),
    }));
    const removeCustomField = (idx) => setConfig(prev => ({
        ...prev,
        customTranslations: prev.customTranslations.filter((_, i) => i !== idx),
    }));
    const [newCustomField, setNewCustomField] = useState('');
    const addCustomField = () => {
        const name = newCustomField.trim();
        if (!name) return;
        const key = `FLD_${name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '')}`;
        setConfig(prev => ({
            ...prev,
            customTranslations: [...prev.customTranslations, { key, field: name, nl: '', en: '', fr: '' }],
        }));
        setNewCustomField('');
    };

    // Translations table — required + custom, filtered by search.
    const allTranslationRows = useMemo(() => {
        const required = REQUIRED_TRANSLATIONS.map(r => ({
            ...r,
            nl: config.translationOverrides[r.key]?.nl ?? r.nl,
            en: config.translationOverrides[r.key]?.en ?? r.en,
            fr: config.translationOverrides[r.key]?.fr ?? r.fr,
            kind: 'required',
        }));
        const custom = config.customTranslations.map(c => ({ ...c, kind: 'custom' }));
        return [...required, ...custom];
    }, [config.translationOverrides, config.customTranslations]);

    const visibleTranslationRows = useMemo(() => {
        const q = config.translationsSearch.trim().toLowerCase();
        if (!q) return allTranslationRows;
        return allTranslationRows.filter(r =>
            r.field?.toLowerCase().includes(q) ||
            r.key?.toLowerCase().includes(q)   ||
            r.nl?.toLowerCase().includes(q)    ||
            r.en?.toLowerCase().includes(q)    ||
            r.fr?.toLowerCase().includes(q)
        );
    }, [allTranslationRows, config.translationsSearch]);

    const SUBTABS = [
        { idx: 0, key: 'settings', icon: <SettingsIcon  fontSize="small" />, label: tr('cfg_voucher_subtab_settings', 'Settings') },
        { idx: 1, key: 'layouts',  icon: <GridViewIcon  fontSize="small" />, label: tr('cfg_voucher_subtab_layouts',  'Voucher layouts') },
        { idx: 2, key: 'trans',    icon: <TranslateIcon fontSize="small" />, label: tr('cfg_voucher_subtab_trans',    'Translations') },
    ];

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, gap: 3, alignItems: 'start' }}>
            {/* ─────────── Sidebar with sub-tabs ─────────── */}
            <Box>
                <Box sx={{ px: 1, mb: 1 }}>
                    <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
                        {tr('cfg_voucher_sidebar_title', 'Gift voucher options')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {tr('cfg_voucher_sidebar_subtitle', 'Configure gift vouchers for your shop')}
                    </Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mx: 1, mb: 1 }} />
                <List dense disablePadding>
                    {SUBTABS.map((s) => {
                        const selected = voucherTab === s.idx;
                        return (
                            <ListItemButton
                                key={s.key}
                                selected={selected}
                                onClick={() => setVoucherTab(s.idx)}
                                sx={{
                                    borderRadius: 1.5, mb: 0.5, py: 1.25,
                                    '&.Mui-selected': { backgroundColor: 'rgba(25,118,210,0.08)' },
                                    '&.Mui-selected:hover': { backgroundColor: 'rgba(25,118,210,0.12)' },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 32, color: selected ? 'primary.main' : 'text.secondary' }}>
                                    {s.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500, color: selected ? 'primary.main' : 'text.primary' }}>
                                            {s.label}
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>

            {/* ─────────── Detail content ─────────── */}
            <Box>
            {/* ─────────────────── SETTINGS ─────────────────── */}
            {voucherTab === 0 && (
                <Stack spacing={2}>
                    {/* Availability */}
                    <ConfigCard title={tr('cfg_voucher_availability_title', 'Availability')} description={tr('cfg_voucher_availability_desc', 'Whether gift vouchers are sold in your shop, and what types are offered.')} theme={theme}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_show_nav', 'Show gift voucher in the navigation menu')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_show_nav_help', "Adds a 'Gift voucher' link to the main navigation so visitors can find it.")}</Typography>
                            </Box>
                            <Switch color="success" size="small" checked={config.availability.showInNavigation} onChange={(e) => setAvailability({ showInNavigation: e.target.checked })} />
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.25 }}>{tr('cfg_voucher_types', 'Available voucher types')}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{tr('cfg_voucher_types_help', 'Pick which kinds of gift vouchers customers can buy.')}</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                                <SelectableTile
                                    selected={config.availability.allowPhysical}
                                    onChange={(v) => setAvailability({ allowPhysical: v })}
                                    label={tr('cfg_voucher_physical', 'Physical voucher')}
                                    desc={tr('cfg_voucher_physical_desc', "Printed on paper and shipped to the customer's address.")}
                                />
                                <SelectableTile
                                    selected={config.availability.allowDigital}
                                    onChange={(v) => setAvailability({ allowDigital: v })}
                                    label={tr('cfg_voucher_digital', 'Digital voucher')}
                                    desc={tr('cfg_voucher_digital_desc', 'Sent by email — to the buyer or directly to a recipient.')}
                                />
                            </Box>
                        </Box>
                    </ConfigCard>

                    {/* Voucher amounts */}
                    <ConfigCard title={tr('cfg_voucher_amounts_title', 'Voucher amounts')} description={tr('cfg_voucher_amounts_desc', 'The amounts customers can choose when buying a gift voucher.')} theme={theme}>
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.25 }}>{tr('cfg_voucher_presets', 'Preset amounts')}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{tr('cfg_voucher_presets_help', 'Common amounts shown as quick choices. Type a number and press Enter to add a new one.')}</Typography>
                            <PresetAmountsInput
                                presets={config.amounts.presets}
                                onAdd={addPreset}
                                onRemove={removePreset}
                                addLabel={tr('cfg_voucher_add_amount', 'Add amount')}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_allow_custom', 'Allow custom amount')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_allow_custom_help', 'Customers can enter their own amount instead of picking a preset.')}</Typography>
                            </Box>
                            <Switch color="success" size="small" checked={config.amounts.allowCustom} onChange={(e) => setAmounts({ allowCustom: e.target.checked })} />
                        </Box>
                        {config.amounts.allowCustom && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pl: 2 }}>
                                <Box>
                                    <Typography variant="body2">{tr('cfg_voucher_amount_range', 'Amount range')}</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_amount_range_help', 'Minimum and maximum amounts a customer can enter.')}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        type="number" size="small"
                                        value={config.amounts.customMin}
                                        onChange={(e) => setAmounts({ customMin: Math.max(0, parseInt(e.target.value) || 0) })}
                                        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                        sx={{ width: 100 }}
                                    />
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_to', 'to')}</Typography>
                                    <TextField
                                        type="number" size="small"
                                        value={config.amounts.customMax}
                                        onChange={(e) => setAmounts({ customMax: Math.max(0, parseInt(e.target.value) || 0) })}
                                        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                        sx={{ width: 100 }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </ConfigCard>

                    {/* Customer discounts */}
                    <ConfigCard title={tr('cfg_voucher_discounts_title', 'Customer discounts')} description={tr('cfg_voucher_discounts_desc', 'How loyalty discounts apply to gift voucher purchases.')} theme={theme}>
                        <RowToggle
                            label={tr('cfg_voucher_disc_count', 'Count voucher purchases toward customer discount totals')}
                            help={tr('cfg_voucher_disc_count_help', 'When on, money spent on gift vouchers contributes to the customer\'s loyalty progress.')}
                            checked={config.discounts.countTowardTotals}
                            onChange={(v) => setDiscounts({ countTowardTotals: v })}
                        />
                        <RowToggle
                            label={tr('cfg_voucher_disc_voucher_only', 'Apply customer discount to voucher-only orders')}
                            help={tr('cfg_voucher_disc_voucher_only_help', 'When on, customers can use their loyalty discount on orders containing only gift vouchers.')}
                            checked={config.discounts.applyToVoucherOnly}
                            onChange={(v) => setDiscounts({ applyToVoucherOnly: v })}
                        />
                        <RowToggle
                            label={tr('cfg_voucher_disc_mixed', 'Apply customer discount to mixed orders')}
                            help={tr('cfg_voucher_disc_mixed_help', 'When on, the loyalty discount applies to orders that contain both vouchers and regular products.')}
                            checked={config.discounts.applyToMixedOrders}
                            onChange={(v) => setDiscounts({ applyToMixedOrders: v })}
                        />
                    </ConfigCard>

                    {/* Shipping */}
                    <ConfigCard title={tr('cfg_voucher_shipping_title', 'Shipping (physical vouchers)')} description={tr('cfg_voucher_shipping_desc', 'Cost of sending physical gift vouchers to the customer.')} theme={theme}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_ship_cost', 'Shipping cost')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_ship_cost_help', 'Charged on each order with a physical voucher.')}</Typography>
                            </Box>
                            <TextField
                                type="number" size="small"
                                value={config.shipping.cost}
                                onChange={(e) => setShipping({ cost: Math.max(0, parseFloat(e.target.value) || 0) })}
                                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                sx={{ width: 110 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_free_threshold', 'Free shipping threshold')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_free_threshold_help', 'Orders above this amount get free shipping. Set to 0 to disable.')}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    type="number" size="small"
                                    value={config.shipping.freeFrom}
                                    onChange={(e) => setShipping({ freeFrom: Math.max(0, parseFloat(e.target.value) || 0) })}
                                    InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                    sx={{ width: 110 }}
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_and_above', 'and above')}</Typography>
                            </Box>
                        </Box>
                    </ConfigCard>

                    {/* Personalization message */}
                    <ConfigCard title={tr('cfg_voucher_pers_title', 'Personalization message')} description={tr('cfg_voucher_pers_desc', 'Whether customers can add a personal message to the voucher (typically used for digital vouchers).')} theme={theme}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_pers_allow', 'Allow personal message')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_pers_allow_help', 'Pick which voucher types let the customer add their own text.')}</Typography>
                            </Box>
                            <TextField
                                select size="small"
                                value={config.personalization.allowPersonalMessage}
                                onChange={(e) => setPersonalization({ allowPersonalMessage: e.target.value })}
                                sx={{ minWidth: 240 }}
                            >
                                <MenuItem value="never">{tr('cfg_voucher_pers_never', 'Never')}</MenuItem>
                                <MenuItem value="digital-only">{tr('cfg_voucher_pers_digital', 'Digital vouchers only (sent by email)')}</MenuItem>
                                <MenuItem value="all">{tr('cfg_voucher_pers_all', 'All vouchers')}</MenuItem>
                            </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, opacity: config.personalization.allowPersonalMessage === 'never' ? 0.5 : 1, pointerEvents: config.personalization.allowPersonalMessage === 'never' ? 'none' : 'auto' }}>
                            <Box>
                                <Typography variant="body2">{tr('cfg_voucher_pers_send_direct', 'Send digital voucher directly to recipient')}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_voucher_pers_send_direct_help', "Lets the customer enter the recipient's email so the voucher arrives in their inbox, not the buyer's.")}</Typography>
                            </Box>
                            <Switch color="success" size="small" checked={config.personalization.sendDigitalDirectly} onChange={(e) => setPersonalization({ sendDigitalDirectly: e.target.checked })} />
                        </Box>
                    </ConfigCard>
                </Stack>
            )}

            {/* ─────────────────── VOUCHER LAYOUTS ─────────────────── */}
            {voucherTab === 1 && (
                <Stack spacing={2}>
                    <LayoutGroup
                        title={tr('cfg_voucher_layouts_physical', 'Physical voucher layouts')}
                        desc={tr('cfg_voucher_layouts_physical_desc', 'Designs customers can pick from when buying a printed voucher.')}
                        layouts={config.physicalLayouts}
                        onAdd={addPhysicalLayout}
                        onRename={(id, name) => renameLayout('physicalLayouts', id, name)}
                        onRemove={(id) => removeLayout('physicalLayouts', id)}
                        addLabel={tr('cfg_voucher_layouts_new', 'New layout')}
                        theme={theme}
                    />
                    <LayoutGroup
                        title={tr('cfg_voucher_layouts_digital', 'Digital voucher layouts')}
                        desc={tr('cfg_voucher_layouts_digital_desc', 'Designs customers can pick from when buying an emailed voucher.')}
                        layouts={config.digitalLayouts}
                        onAdd={addDigitalLayout}
                        onRename={(id, name) => renameLayout('digitalLayouts', id, name)}
                        onRemove={(id) => removeLayout('digitalLayouts', id)}
                        addLabel={tr('cfg_voucher_layouts_new', 'New layout')}
                        theme={theme}
                    />
                    <ConfigCard title={tr('cfg_voucher_layouts_defaults', 'System defaults')} description={tr('cfg_voucher_layouts_defaults_desc', 'Built-in templates that ship with the platform. These can be duplicated to customize, but not deleted.')} theme={theme}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
                            {config.systemDefaults.map((s) => (
                                <Paper key={s.id} variant="outlined" sx={{
                                    position: 'relative',
                                    aspectRatio: '4 / 3',
                                    backgroundColor: '#000', color: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                    '&:hover .sys-dup': { opacity: 1 },
                                }}>
                                    <Box sx={{ position: 'absolute', top: 4, left: 4, fontSize: '0.55rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: 0.5 }}>
                                        {tr('cfg_voucher_layouts_default_badge', 'DEFAULT')}
                                    </Box>
                                    <Box sx={{ fontWeight: 700, fontSize: '1.1rem' }}>LOGO</Box>
                                    <IconButton
                                        className="sys-dup"
                                        size="small"
                                        onClick={() => duplicateSystemDefault(s.id)}
                                        title={tr('cfg_voucher_layouts_duplicate', 'Duplicate to digital layouts')}
                                        sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, color: 'rgba(255,255,255,0.85)' }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                    <Box sx={{ position: 'absolute', bottom: 4, left: 4, right: 4, fontSize: '0.65rem', color: 'rgba(255,255,255,0.85)', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {s.name}
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </ConfigCard>
                </Stack>
            )}

            {/* ─────────────────── TRANSLATIONS ─────────────────── */}
            {voucherTab === 2 && (
                <ConfigCard
                    title={tr('cfg_voucher_trans_title', 'Translations')}
                    description={tr('cfg_voucher_trans_desc', 'Labels shown on vouchers in each language. Edit the translations directly in the table below.')}
                    headerActions={
                        <TextField
                            size="small"
                            placeholder={tr('cfg_voucher_trans_search', 'Search translations…')}
                            value={config.translationsSearch}
                            onChange={(e) => setConfig(prev => ({ ...prev, translationsSearch: e.target.value }))}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            sx={{ minWidth: 240 }}
                        />
                    }
                    theme={theme}
                >
                    <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}>{tr('cfg_voucher_trans_field',  'FIELD')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}>{tr('cfg_voucher_trans_dutch',  'DUTCH (NL)')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}>{tr('cfg_voucher_trans_english','ENGLISH (EN)')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}>{tr('cfg_voucher_trans_french', 'FRENCH (FR)')}</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }} align="right">{tr('cfg_voucher_trans_actions','ACTIONS')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleTranslationRows.map((row, idx) => {
                                    const isCustom = row.kind === 'custom';
                                    const customIdx = isCustom ? config.customTranslations.findIndex(c => c.key === row.key) : -1;
                                    const onLangChange = (lang) => (e) => {
                                        if (isCustom) setCustomField(customIdx, lang, e.target.value);
                                        else          setOverride(row.key, lang, e.target.value);
                                    };
                                    return (
                                        <TableRow key={`${row.key}-${idx}`} hover>
                                            <TableCell sx={{ verticalAlign: 'top', minWidth: 180 }}>
                                                {/* Title row: field name + badge sit on the same line.
                                                    Badge wraps to a new line only on very narrow viewports. */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                    {isCustom ? (
                                                        <TextField
                                                            size="small" variant="standard"
                                                            value={row.field}
                                                            onChange={(e) => setCustomFieldName(customIdx, e.target.value)}
                                                            sx={{ '& .MuiInput-input': { fontWeight: 600, fontSize: '0.85rem' } }}
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{row.field}</Typography>
                                                    )}
                                                    <Chip
                                                        label={isCustom ? tr('cfg_voucher_trans_custom', 'CUSTOM') : tr('cfg_voucher_trans_required', 'REQUIRED')}
                                                        size="small"
                                                        color={isCustom ? 'primary' : 'default'}
                                                        variant={isCustom ? 'filled' : 'outlined'}
                                                        sx={{ fontSize: '0.55rem', height: 16, '& .MuiChip-label': { px: 0.75 } }}
                                                    />
                                                </Box>
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace', fontSize: '0.65rem', mt: 0.25 }}>
                                                    {row.key}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <TextField size="small" fullWidth value={row.nl || ''} onChange={onLangChange('nl')} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} />
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <TextField size="small" fullWidth value={row.en || ''} onChange={onLangChange('en')} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} />
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <TextField size="small" fullWidth value={row.fr || ''} onChange={onLangChange('fr')} sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} />
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }} align="right">
                                                {isCustom ? (
                                                    <IconButton size="small" color="error" onClick={() => removeCustomField(customIdx)} title={tr('cfg_voucher_trans_remove', 'Remove field')}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton size="small" disabled title={tr('cfg_voucher_trans_required_locked', 'Required field — cannot be removed')}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>

                    {/* Add custom translation row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1 }}>
                        <AddIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <TextField
                            size="small" fullWidth
                            placeholder={tr('cfg_voucher_trans_add_placeholder', 'Custom field name (e.g. Special offer)')}
                            value={newCustomField}
                            onChange={(e) => setNewCustomField(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomField(); } }}
                            sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
                        />
                        <Button variant="contained" size="small" onClick={addCustomField} disabled={!newCustomField.trim()} sx={{ flexShrink: 0, textTransform: 'none' }}>
                            {tr('cfg_voucher_trans_add', 'Add custom translation')}
                        </Button>
                    </Box>
                </ConfigCard>
            )}
            </Box>
        </Box>
    );
};

// ---------- helpers ----------

const SelectableTile = ({ selected, onChange, label, desc }) => (
    <Paper
        variant="outlined"
        onClick={() => onChange(!selected)}
        sx={{
            p: 1.5,
            display: 'flex', alignItems: 'flex-start', gap: 1,
            cursor: 'pointer',
            borderColor: selected ? 'primary.main' : 'divider',
            backgroundColor: selected ? 'rgba(25,118,210,0.06)' : 'transparent',
            borderWidth: selected ? 2 : 1,
            transition: 'all 120ms',
            borderRadius: 1.5,
        }}
    >
        <Checkbox size="small" checked={selected} onChange={(e) => onChange(e.target.checked)} sx={{ p: 0.25, mt: -0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35 }}>{desc}</Typography>
        </Box>
    </Paper>
);

const PresetAmountsInput = ({ presets, onAdd, onRemove, addLabel }) => {
    const [val, setVal] = useState('');
    const submit = () => { if (val) { onAdd(val); setVal(''); } };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {presets.map((p) => (
                <Chip
                    key={p}
                    label={`€${p}`}
                    size="small"
                    onDelete={() => onRemove(p)}
                    deleteIcon={<CloseIcon fontSize="small" />}
                    sx={{ fontWeight: 600 }}
                />
            ))}
            <TextField
                type="number" size="small"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                placeholder={addLabel}
                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                sx={{ width: 110, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }}
            />
        </Box>
    );
};

const RowToggle = ({ label, help, checked, onChange }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
            <Typography variant="body2">{label}</Typography>
            {help && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{help}</Typography>}
        </Box>
        <Switch color="success" size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </Box>
);

const LayoutGroup = ({ title, desc, layouts, onAdd, onRename, onRemove, addLabel, theme }) => (
    <ConfigCard title={title} description={desc} theme={theme}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
            {/* "+ New layout" tile */}
            <Paper variant="outlined" sx={{
                aspectRatio: '4 / 3', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                borderStyle: 'dashed', borderRadius: 1.5,
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
            }} onClick={onAdd}>
                <AddIcon fontSize="small" />
                <Typography variant="caption">{addLabel}</Typography>
            </Paper>

            {layouts.map((l) => (
                <Box key={l.id}>
                    <Paper variant="outlined" sx={{
                        position: 'relative',
                        aspectRatio: '4 / 3',
                        backgroundColor: l.preview || '#000',
                        color: l.preview === '#fff' ? '#000' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 1.5, overflow: 'hidden',
                        '&:hover .lay-rm': { opacity: 1 },
                    }}>
                        <Box sx={{ fontWeight: 700, fontSize: '1.1rem', textShadow: l.preview === '#fff' ? 'none' : '0 1px 2px rgba(0,0,0,0.3)' }}>LOGO</Box>
                        <IconButton
                            className="lay-rm"
                            size="small"
                            onClick={() => onRemove(l.id)}
                            sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, color: 'rgba(255,255,255,0.85)', backgroundColor: 'rgba(0,0,0,0.3)' }}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Paper>
                    <TextField
                        size="small" variant="standard" fullWidth
                        value={l.name}
                        onChange={(e) => onRename(l.id, e.target.value)}
                        sx={{ mt: 0.5, '& .MuiInput-input': { fontSize: '0.78rem', py: 0.25 } }}
                    />
                </Box>
            ))}
        </Box>
    </ConfigCard>
);

CadeaubonTab.propTypes = { theme: PropTypes.string, t: PropTypes.func };
export default CadeaubonTab;
